import { useEffect, useState } from 'react';
import { userApi } from '../../api/user.api';
import { departmentApi } from '../../api/department.api';
import { useAuth } from '../../store/AuthContext';
import { useUI } from '../../store/UIContext';
import { Modal, Btn, Input, PhoneInput, Badge, Select } from '../ui/index';
import { phoneError } from '../../utils/validators';

export default function UserActionsModal({ target, onClose, onChanged }) {
  const { addToast } = useUI();
  const { user } = useAuth() || {};
  const canChangeDepartment = user?.type === 'dispatcher' || user?.type === 'admin';
  const [departments, setDepartments] = useState([]);
  const [departmentName, setDepartmentName] = useState('');
  const [savingDepartment, setSavingDepartment] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [savingPass, setSavingPass] = useState(false);
  const [savingPhone, setSavingPhone] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [savingActive, setSavingActive] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!target) return;
    setFirstName(target.first_name || '');
    setLastName(target.last_name || '');
    setDepartmentName(target.department_name || '');
    setNewPassword('');
    setNewPhone('');
    setOtp('');
    setOtpSent(false);
  }, [target]);

  useEffect(() => {
    if (!target || !canChangeDepartment || target.type !== 'employee' || target.is_manager) return;
    departmentApi.getDepartmentListView()
      .then((r) => setDepartments(Array.isArray(r.data) ? r.data : []))
      .catch(() => {});
  }, [target, canChangeDepartment]);

  if (!target) return null;
  const isEmployee = target.type === 'employee';
  const phoneErr = phoneError(newPhone);
  const showDepartmentChange = isEmployee && canChangeDepartment && !target.is_manager;

  const saveDepartment = async () => {
    if (!departmentName) {
      addToast('دپارتمان را انتخاب کنید', 'error');
      return;
    }
    if (departmentName === target.department_name) {
      addToast('دپارتمان تغییری نکرده', 'error');
      return;
    }
    setSavingDepartment(true);
    try {
      await userApi.updateEmployeeDepartment({ username: target.username, name: departmentName });
      addToast('دپارتمان کارمند تغییر کرد', 'success');
      onChanged?.();
      onClose();
    } catch (err) {
      const msg = typeof err?.response?.data === 'string' ? err.response.data : 'خطا در تغییر دپارتمان';
      addToast(msg, 'error');
    }
    setSavingDepartment(false);
  };

  const changePassword = async () => {
    if (!newPassword.trim()) { addToast('رمز عبور جدید را وارد کنید', 'error'); return; }
    setSavingPass(true);
    try {
      const call = isEmployee ? userApi.changeEmployeePassword : userApi.changeUserPassword;
      await call({ username: target.username, new_pass: newPassword });
      addToast('رمز عبور تغییر کرد', 'success');
      setNewPassword('');
    } catch { addToast('خطا در تغییر رمز عبور', 'error'); }
    setSavingPass(false);
  };

  const saveProfile = async () => {
    if (!firstName.trim() || !lastName.trim()) {
      addToast('نام و نام خانوادگی الزامی است', 'error');
      return;
    }
    setSavingProfile(true);
    try {
      if (isEmployee) {
        await userApi.editEmployeeInfo({ id: target.id, first_name: firstName.trim(), last_name: lastName.trim() });
      } else {
        await userApi.editUserInfo({ id: target.id, first_name: firstName.trim(), last_name: lastName.trim() });
      }
      addToast('اطلاعات ذخیره شد', 'success');
      onChanged?.();
    } catch { addToast('خطا در ذخیره اطلاعات', 'error'); }
    setSavingProfile(false);
  };

  const saveAndSendOtp = async () => {
    if (!newPhone.trim()) { addToast('شماره موبایل را وارد کنید', 'error'); return; }
    if (phoneErr) return;
    setSavingPhone(true);
    try {
      await userApi.adminEditPhoneNumber({ id: target.id, user_type: target.type, value: newPhone });
      await userApi.adminSendPhoneOtp({ id: target.id, user_type: target.type });
      setOtpSent(true);
      setOtp('');
      addToast('شماره ذخیره شد و کد تایید ارسال شد', 'success');
    } catch { addToast('خطا در ثبت یا ارسال کد تایید', 'error'); }
    setSavingPhone(false);
  };

  const verifyPhone = async () => {
    if (otp.length < 7) { addToast('کد ۷ رقمی را کامل وارد کنید', 'error'); return; }
    setVerifying(true);
    try {
      await userApi.adminVerifyPhoneOtp({ id: target.id, user_type: target.type, auth_code: otp });
      addToast('شماره موبایل احراز هویت شد', 'success');
      setOtpSent(false);
      setOtp('');
      setNewPhone('');
      onChanged?.();
    } catch { addToast('کد وارد شده صحیح یا منقضی شده است', 'error'); }
    setVerifying(false);
  };

  const toggleActive = async () => {
    setSavingActive(true);
    try {
      const call = isEmployee ? userApi.changeEmployeeActivation : userApi.changeUserActivation;
      await call({ user_id: target.id, activate: target.active ? 0 : 1, description: '' });
      addToast(target.active ? 'غیرفعال شد' : 'فعال شد', 'success');
      onChanged?.();
      onClose();
    } catch { addToast('خطا در تغییر وضعیت', 'error'); }
    setSavingActive(false);
  };

  const removeEmployee = async () => {
    if (!isEmployee) return;
    if (!window.confirm(`کارمند «${target.username}» غیرفعال (حذف) شود؟`)) return;
    setDeleting(true);
    try {
      await userApi.deleteEmployee({ employee_id: target.id, description: 'حذف توسط دیسپچر' });
      addToast('کارمند حذف شد (غیرفعال)', 'success');
      onChanged?.();
      onClose();
    } catch (e) {
      const msg = typeof e?.response?.data === 'string' ? e.response.data : 'خطا در حذف کارمند';
      addToast(msg, 'error');
    }
    setDeleting(false);
  };

  return (
    <Modal open={!!target} onClose={onClose} title={`مدیریت ${isEmployee ? 'کارمند' : 'کاربر'}: ${target.username}`} width={460}>
      <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 13, color: '#64748b' }}>وضعیت فعلی:</span>
          <Badge variant={target.active ? 'green' : 'gray'}>{target.active ? 'فعال' : 'غیرفعال'}</Badge>
        </div>

        <div>
          <Input label="نام" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
          <Input label="نام خانوادگی" value={lastName} onChange={(e) => setLastName(e.target.value)} style={{ marginTop: 10 }} />
          <Btn size="sm" onClick={saveProfile} loading={savingProfile} style={{ marginTop: 8 }}>ذخیره نام</Btn>
        </div>

        {showDepartmentChange && (
          <div>
            <Select
              label="دپارتمان"
              value={departmentName}
              onChange={(e) => setDepartmentName(e.target.value)}
              options={[
                { value: '', label: '— انتخاب دپارتمان —' },
                ...departments.map((d) => ({ value: d.name, label: d.name })),
              ]}
            />
            <Btn size="sm" onClick={saveDepartment} loading={savingDepartment} style={{ marginTop: 8 }}>
              ذخیره دپارتمان
            </Btn>
          </div>
        )}

        {isEmployee && target.is_manager && canChangeDepartment && (
          <p style={{ fontSize: 12, color: '#64748b', margin: 0, lineHeight: 1.6 }}>
            این کاربر مدیر دپارتمان است؛ برای جابه‌جایی از «لیست دپارتمان‌ها → تغییر مدیر» استفاده کنید.
          </p>
        )}

        <div>
          <Input label="تغییر رمز عبور" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="رمز عبور جدید" />
          <Btn size="sm" onClick={changePassword} loading={savingPass} style={{ marginTop: 8 }}>ثبت رمز جدید</Btn>
        </div>

        <div>
          <PhoneInput label="شماره موبایل (ثبت و احراز هویت)" value={newPhone} onChange={(e) => setNewPhone(e.target.value)} placeholder="09xxxxxxxxx" error={phoneErr} />
          <p style={{ fontSize: 12, color: '#64748b', margin: '6px 0 0', lineHeight: 1.6 }}>
            پس از ثبت، کد تایید به همین شماره پیامک می‌شود. تا احراز هویت انجام نشود این حساب نمی‌تواند کار کند.
          </p>
          <Btn size="sm" onClick={saveAndSendOtp} loading={savingPhone} disabled={!!phoneErr} style={{ marginTop: 8 }}>ثبت و ارسال کد</Btn>
          {otpSent && (
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <input
                type="text"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 7))}
                placeholder="کد ۷ رقمی"
                dir="ltr"
                maxLength={7}
                aria-label="کد تایید موبایل"
                style={{ flex: 1, padding: '9px 12px', border: '1.5px solid #B7C9EC', borderRadius: 8, fontSize: 15, fontFamily: 'monospace', letterSpacing: 6, textAlign: 'center', outline: 'none' }}
              />
              <Btn size="sm" onClick={verifyPhone} loading={verifying} disabled={otp.length < 7}>تایید کد</Btn>
            </div>
          )}
        </div>

        <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: 16, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          <Btn size="sm" variant={target.active ? 'danger' : 'success'} onClick={toggleActive} loading={savingActive}>
            {target.active ? 'غیرفعال کردن' : 'فعال کردن'}
          </Btn>
          {isEmployee && target.active && (
            <Btn size="sm" variant="danger" onClick={removeEmployee} loading={deleting}>حذف کارمند</Btn>
          )}
        </div>
      </div>
    </Modal>
  );
}
